import type { WeightEntry, WeeklyNote, WeeklyRow } from "./types";
import { mondayOf, weekDates, weekLabelFor } from "./dates";
import { median } from "./format";

// Groups entries into Monday..Sunday weeks, ascending (oldest week first),
// each with its median weight and the delta vs. the previous week's median.
// A day may carry a note without a weight, so weights/median only ever look
// at the days that actually have one.
export function computeWeeklyRows(entries: WeightEntry[]): WeeklyRow[] {
	const byWeek = new Map<string, WeightEntry[]>();
	for (const entry of entries) {
		const weekStart = mondayOf(entry.date);
		const list = byWeek.get(weekStart) ?? [];
		list.push(entry);
		byWeek.set(weekStart, list);
	}

	const weekStarts = Array.from(byWeek.keys()).sort((a, b) => a.localeCompare(b));

	const rows: WeeklyRow[] = weekStarts.map((weekStart) => {
		const weekEntries = byWeek.get(weekStart)!;
		const entriesByDate = new Map(weekEntries.map((e) => [e.date, e]));
		const dayDates = weekDates(weekStart);
		const days = dayDates.map((date) => entriesByDate.get(date) ?? null);
		const weights = weekEntries.filter((e) => e.weight !== undefined).map((e) => e.weight!);
		const notes: WeeklyNote[] = days
			.map((day, dayIndex) => (day?.note ? { date: day.date, dayIndex, text: day.note } : null))
			.filter((note): note is WeeklyNote => note !== null);
		return {
			weekStart,
			weekLabel: weekLabelFor(weekStart),
			dayDates,
			days,
			weights,
			median: median(weights),
			delta: null,
			notes,
		};
	});

	// Delta compares against the nearest earlier week that has a median,
	// skipping over note-only weeks instead of leaving a gap in the trend.
	let previousMedian: number | null = null;
	for (const row of rows) {
		row.delta = row.median === null || previousMedian === null ? null : row.median - previousMedian;
		if (row.median !== null) previousMedian = row.median;
	}

	return rows;
}
